import { useContext, useState, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { InputTextField } from '@abbott-components/core';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import dayjs, { Dayjs } from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopTimePicker } from '@mui/x-date-pickers/DesktopTimePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import ControlState from '../../Shared/controlState';
import { WorkflowEngineContext } from '../../WorkflowEngine/workflowEngineReducer';
import JSONSchema7Properties from '../../Shared/jSONSchema7Properties';
import { JSONSchema7 } from 'json-schema';
import isIE11 from '../../Shared/isIE11';
import { Box } from '@mui/material';

export default (control: ControlState) => {
  const { state } = useContext(WorkflowEngineContext);
  const { isReadOnlyView } = state;
  const formContext = useFormContext();
  const properties = control.schema?.properties as JSONSchema7Properties;
  const labelSchema = properties['label'] as JSONSchema7;
  const formatSchema = properties['format'] as JSONSchema7;
  const is24HrsFormat = formatSchema.default === '24Hrs';
  const initialTime = formContext?.getValues(control.id);
  const fieldState = formContext?.getFieldState(control.id);
  const [value, setValue] = useState<Dayjs | null>(null);

  const handleTimeChange = (newValue: Dayjs | null) => {
    if (!newValue) {
      formContext.setValue(control.id, '');
      setValue(null);
      return;
    }
    formContext.setValue(control.id, newValue.format('HH:mm'));
    setValue(newValue);
  };

  const renderHiddenFields = () => (
    <Stack sx={{ display: 'none' }}>
      <InputTextField name={control.id} />
    </Stack>
  );

  const renderValidationErrors = () => {
    return (
      !!fieldState?.error && (
        <Typography sx={{ marginTop: 2, color: 'abbott.red' }}>
          {fieldState?.error?.message}
        </Typography>
      )
    );
  };

  useEffect(() => {
    if (!initialTime) return;

    const storedHours = parseInt(initialTime.split(':')[0]);
    const storedMinutes = parseInt(initialTime.split(':')[1]);

    const newTime = dayjs().hour(storedHours).minute(storedMinutes);
    setValue(newTime);
  }, []);

  return (
    <Box sx={{ width: '100%', pt: '1rem' }}>
      {renderHiddenFields()}
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        {isIE11() ? (
          <DesktopTimePicker
            PopperProps={{
              placement: 'bottom-end'
            }}
            label={labelSchema?.default as string}
            value={value}
            disabled={isReadOnlyView}
            ampm={!is24HrsFormat}
            onChange={handleTimeChange}
            renderInput={(params) => <TextField fullWidth {...params} />}
          />
        ) : (
          <TimePicker
            PopperProps={{
              placement: 'bottom-end'
            }}
            label={labelSchema?.default as string}
            value={value}
            disabled={isReadOnlyView}
            ampm={!is24HrsFormat}
            orientation="landscape"
            onChange={handleTimeChange}
            renderInput={(params) => <TextField fullWidth {...params} />}
          />
        )}
      </LocalizationProvider>
      {renderValidationErrors()}
    </Box>
  );
};
